// @file Composable for handling drag and drop events on the whiteboard's canvas
import { __ } from '@@/bits/intl'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { useWhiteboardFileUpload } from '@@/vuecomposables/useWhiteboardFileUpload'
import type { Ref } from 'vue'
import { onMounted, onUnmounted, ref } from 'vue'

function useWhiteboardDragAndDrop(tldrawAppRef: Ref<HTMLElement | undefined>): {
  isDragging: Ref<boolean>
} {
  const isDragging = ref(false)
  const whiteboardStore = useWhiteboardStore()
  const whiteboardToolbarStore = useWhiteboardToolbarStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const { canIUploadFiles, getFileUploadBlocker } = useWhiteboardFileUpload()

  function startDragging(): void {
    isDragging.value = true
  }

  function stopDragging(): void {
    isDragging.value = false
    whiteboardToolbarStore.resetDropOverlayMessage()
  }

  function handleDragEnter(event: DragEvent): void {
    // Don't show file drop overlay during frame reordering
    if (whiteboardStore.isFrameDragging) {
      return
    }

    if (canIUploadFiles()) {
      event.preventDefault()
      startDragging()
    }
  }

  function handleDrop(event: DragEvent): void {
    stopDragging()
    if (!event.dataTransfer?.files?.length) return

    const blocker = getFileUploadBlocker()
    if (blocker === 'no-write-permission') {
      globalSnackbarStore.setSnackbar({
        message: __('You don’t have permission to add content to this sandbox'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    if (blocker === 'upload-tool-disabled') {
      globalSnackbarStore.setSnackbar({
        message: __('Upload is currently disabled on this sandbox'),
        notificationType: SnackbarNotificationType.error,
      })
    }
  }

  function handleDragLeave(event: DragEvent): void {
    if (tldrawAppRef.value == null) return
    const rect = tldrawAppRef.value.getBoundingClientRect()
    const isOutsideCanvas =
      event.clientX <= rect.left ||
      event.clientX >= rect.right ||
      event.clientY <= rect.top ||
      event.clientY >= rect.bottom

    if (isOutsideCanvas) {
      stopDragging()
    }
  }

  onMounted(() => {
    tldrawAppRef.value?.addEventListener('dragenter', handleDragEnter)
    tldrawAppRef.value?.addEventListener('drop', handleDrop)
    tldrawAppRef.value?.addEventListener('dragleave', handleDragLeave)
  })

  onUnmounted(() => {
    tldrawAppRef.value?.removeEventListener('dragenter', handleDragEnter)
    tldrawAppRef.value?.removeEventListener('drop', handleDrop)
    tldrawAppRef.value?.removeEventListener('dragleave', handleDragLeave)
  })

  return {
    isDragging,
  }
}
export { useWhiteboardDragAndDrop }
